// SEO landing page for an individual task-template category.
//
// Today the in-app catalog (/tasks) routes a category click to the
// store-backed `selectedCategory`. That keeps the authenticated experience
// working but gives crawlers a single URL with nothing category-specific
// in the prerendered HTML. This route fixes that:
//
//   /tasks/category/<slug>
//
// is statically pre-rendered for every enabled category, with its own
// title/description/canonical/JSON-LD, and re-uses the existing
// `TasksPage` (filtered by category) so the in-app behaviour is
// identical. The query-string form keeps working unchanged.
//
// Mirrors the SEO pattern used by /tools/[slug], /blog/[slug],
// /tasks/template/[slug], /agents/category/[slug], and
// /explore/category/[slug].
import React, { useMemo } from 'react';
import { useLocalSearchParams } from 'expo-router';
import MetaHead from '@/components/MetaHead';
import { taskTemplateApi as taskTemplateApiSsr } from '@/lib/taskTemplateApi';
import { TasksPage } from '@/app/(app)/tasks/index';

// ---------------------------------------------------------------------------
// SEO — static generation
// ---------------------------------------------------------------------------
// Module-level cache populated by `generateStaticParams` so the build-time
// HTML render has access to the same data the runtime render will. Keyed by
// slug; each entry holds `{ category, templates }`.
const _staticCategoryCache = {};

export async function generateStaticParams() {
  // Rendered on demand (client SPA), not prerendered. Returning [] keeps
  // `expo export` fast and avoids hammering the API at build time.
  return [];
}

// ---------------------------------------------------------------------------
// MetaHead helpers
// ---------------------------------------------------------------------------
const SITE_URL = 'https://gab.ai';

const titleCase = (s) =>
  String(s || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((p) => p[0].toUpperCase() + p.slice(1))
    .join(' ');

const buildCategoryTitle = (cat, slug) => {
  if (cat?.meta?.title) return cat.meta.title;
  const name = cat?.name || titleCase(slug);
  if (name) return `${name} Super Intelligence Task Templates — Gab AI`;
  return 'Super Intelligence Task Templates';
};

const buildCategoryDescription = (cat, slug) => {
  if (cat?.meta?.description) return cat.meta.description;
  if (cat?.description) return cat.description;
  const name = (cat?.name || titleCase(slug) || '').toLowerCase();
  if (name) {
    return `Browse ready-to-run ${name} task templates on Gab AI. Pick one, customize the schedule, and let it run on its own.`;
  }
  return 'Browse Gab AI’s catalog of automated task templates. Pick one, customize the schedule, and let it run on its own.';
};

const buildKeywords = (cat, slug) => {
  if (cat?.meta?.keywords) return cat.meta.keywords;
  const name = cat?.name || titleCase(slug);
  const base = ['Super Intelligence task templates', 'Super Intelligence automations', 'scheduled Super Intelligence tasks', 'Gab AI tasks'];
  if (name) base.unshift(name, `${name} Super Intelligence`, `Super Intelligence ${name}`);
  return base.join(', ');
};

const buildStructuredData = (cat, templates, slug) => {
  const url = `${SITE_URL}/tasks/category/${slug}`;
  const name = cat?.name || titleCase(slug);
  const itemList = (templates || []).slice(0, 30).map((tpl, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    url: tpl?.slug ? `${SITE_URL}/tasks/template/${tpl.slug}` : url,
    name: tpl?.name || tpl?.slug || `Task ${idx + 1}`,
  }));

  const collection = {
    '@type': 'CollectionPage',
    name: buildCategoryTitle(cat, slug),
    description: buildCategoryDescription(cat, slug),
    url,
    isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: SITE_URL },
    inLanguage: 'en-US',
    ...(itemList.length
      ? { mainEntity: { '@type': 'ItemList', itemListElement: itemList, numberOfItems: itemList.length } }
      : {}),
  };

  const breadcrumbs = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Tasks', item: `${SITE_URL}/tasks` },
      { '@type': 'ListItem', position: 3, name: name || slug, item: url },
    ],
  };

  return { '@context': 'https://schema.org', '@graph': [collection, breadcrumbs] };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function TasksCategoryRoute() {
  const params = useLocalSearchParams();
  const slug =
    typeof params?.slug === 'string'
      ? params.slug
      : Array.isArray(params?.slug)
        ? params.slug[0]
        : null;

  const cached = useMemo(() => {
    if (!slug) return null;
    return _staticCategoryCache[slug] || null;
  }, [slug]);

  const seo = useMemo(() => {
    if (!slug) {
      return {
        title: 'Super Intelligence Task Templates — Gab AI',
        description: 'Browse Gab AI’s catalog of automated task templates.',
        canonical: '/tasks',
        keywords: 'Super Intelligence task templates, Super Intelligence automations, scheduled Super Intelligence tasks, Gab AI tasks',
        structuredData: null,
      };
    }
    const category = cached?.category || null;
    const templates = cached?.templates || [];
    return {
      title: buildCategoryTitle(category, slug),
      description: buildCategoryDescription(category, slug),
      canonical: `/tasks/category/${slug}`,
      keywords: buildKeywords(category, slug),
      structuredData: buildStructuredData(category, templates, slug),
    };
  }, [cached, slug]);

  return (
    <>
      <MetaHead
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        keywords={seo.keywords}
        structuredData={seo.structuredData}
      />
      <TasksPage categorySlug={slug || undefined} />
    </>
  );
}
